import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, PaperclipIcon, SmileIcon, PhoneIcon, MoreVerticalIcon, CheckIcon, CheckCheckIcon, Image as ImageIcon, FileTextIcon, ZapIcon } from 'lucide-react';
import { WhatsAppLead, WhatsAppMessage } from '../../types/whatsapp';
interface WhatsAppChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: WhatsAppLead | null;
}
const MOCK_MESSAGES: WhatsAppMessage[] = [{
  id: '1',
  content: 'Hi, I saw the ad for the new Scorpio-N. Is it available for a test drive?',
  timestamp: '10:30 AM',
  from: 'customer',
  status: 'read',
  type: 'text'
}, {
  id: '2',
  content: 'Hello! Yes, we have the Scorpio-N available for test drives. When would you like to visit?',
  timestamp: '10:32 AM',
  from: 'dealer',
  status: 'read',
  type: 'text'
}, {
  id: '3',
  content: 'Can I come this Saturday around 11 AM?',
  timestamp: '10:35 AM',
  from: 'customer',
  status: 'read',
  type: 'text'
}, {
  id: '4',
  content: 'Saturday 11 AM is confirmed. I will send you the location details shortly.',
  timestamp: '10:36 AM',
  from: 'dealer',
  status: 'delivered',
  type: 'text'
}];
const QUICK_REPLIES = ['Is this vehicle still available?', 'Can I schedule a test drive?', 'What are the financing options?', 'Please send me the brochure.'];
export function WhatsAppChatModal({
  isOpen,
  onClose,
  lead
}: WhatsAppChatModalProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<WhatsAppMessage[]>(MOCK_MESSAGES);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, messages]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleSend = () => {
    if (!message.trim()) return;
    const newMessage: WhatsAppMessage = {
      id: Date.now().toString(),
      content: message,
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      from: 'dealer',
      status: 'sent',
      type: 'text'
    };
    setMessages([...messages, newMessage]);
    setMessage('');
    // Simulate delivery status update
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === newMessage.id ? {
        ...m,
        status: 'delivered'
      } : m));
    }, 1000);
  };
  const handleQuickReply = (text: string) => {
    setMessage(text);
  };
  if (!isOpen || !lead) return null;
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} data-id="element-788">
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex overflow-hidden border-2 border-gray-200 animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-789">
        {/* Left Sidebar - Lead Context */}
        

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-[#e5ded8]" data-id="element-790">
          {/* Chat Header */}
          <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm z-10" data-id="element-791">
            <div className="flex items-center space-x-3" data-id="element-792">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 font-medium" data-id="element-793">
                {lead.name.charAt(0)}
              </div>
              <div data-id="element-794">
                <h3 className="font-semibold text-gray-900" data-id="element-795">{lead.name}</h3>
                <p className="text-xs text-green-600 font-medium flex items-center" data-id="element-796">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse" data-id="element-797"></span>
                  Online
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-500" data-id="element-798">
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-799">
                <XIcon size={20} data-id="element-800" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat" data-id="element-801">
            {messages.map(msg => <div key={msg.id} className={`flex ${msg.from === 'dealer' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`} data-id="element-802">
                <div className={`max-w-[70%] rounded-2xl px-4 py-3 shadow-md relative ${msg.from === 'dealer' ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none border border-gray-100'}`} data-id="element-803">
                  <p className="text-sm text-gray-900 leading-relaxed" data-id="element-804">
                    {msg.content}
                  </p>
                  <div className="flex items-center justify-end space-x-1 mt-1" data-id="element-805">
                    <span className="text-[10px] text-gray-500" data-id="element-806">
                      {msg.timestamp}
                    </span>
                    {msg.from === 'dealer' && <span className="text-blue-500" data-id="element-807">
                        {msg.status === 'read' ? <CheckCheckIcon size={14} data-id="element-808" /> : <CheckIcon size={14} data-id="element-809" />}
                      </span>}
                  </div>
                </div>
              </div>)}
            <div ref={messagesEndRef} data-id="element-810" />
          </div>

          {/* Quick Replies */}
          

          {/* Input Area */}
          <div className="bg-white p-4 border-t border-gray-200 shadow-lg" data-id="element-811">
            <div className="flex items-center space-x-2" data-id="element-812">
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-813">
                <SmileIcon size={24} data-id="element-814" />
              </button>
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-815">
                <PaperclipIcon size={24} data-id="element-816" />
              </button>
              <input type="text" value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="Type a message" className="flex-1 bg-gray-100 border-0 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:bg-white transition-all" data-id="element-817" />
              {message.trim() ? <button onClick={handleSend} className="p-3 bg-green-500 text-white rounded-full hover:bg-green-600 shadow-md transition-all transform hover:scale-105 active:scale-95" data-id="element-818">
                  <SendIcon size={20} data-id="element-819" />
                </button> : <button className="p-3 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-820">
                  <PhoneIcon size={20} data-id="element-821" />
                </button>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}